/**
 * Los avisos emergentes («toasts»): lo que dice que una acción salió bien —o mal—
 * sin obligar a mirar dónde cambió algo.
 *
 * ⚠ **Hasta el 12/10/2026 el éxito casi nunca se decía.** Guardar, aprobar o pagar
 * cerraban el modal y ya: la única confirmación era que la fila cambiaba de sitio,
 * y quien no la estaba mirando volvía a pulsar. Los pocos avisos que había eran
 * cuatro mecanismos distintos, cada pantalla el suyo.
 *
 * ⚠ **Es un emisor de módulo, no un store de Zustand.** Zustand guarda tres cosas y
 * ésta no es una de ellas (reglas del frontend): un aviso no es estado de la
 * aplicación, es un mensaje que se pinta y se va. Y así lo puede disparar el
 * `MutationCache` de TanStack Query, que vive fuera de React.
 *
 * La forma normal de usarlo NO es llamar a `avisar` desde cada pantalla: es poner
 * `meta: { exito: "…" }` en la mutación (ver `main.tsx`).
 */

export type TipoAviso = "exito" | "error" | "info"

export type Aviso = {
  id: number
  tipo: TipoAviso
  titulo: string
  texto?: string
  /** El código del servidor (`SALDO_INSUFICIENTE`…): lo que se dicta al reportar. */
  codigo?: string
  /** Una sola acción, como «Deshacer». El aviso se cierra al pulsarla. */
  accion?: { etiqueta: string; alHacer: () => void }
  /** Milisegundos antes de irse solo; `null` = se queda hasta que se cierra. */
  duracion: number | null
}

export type NuevoAviso = Omit<Aviso, "id" | "duracion"> & { duracion?: number | null }

/**
 * Cuánto dura cada tipo. ⚠ **El error NO se va solo**: hay que leerlo, y a veces
 * dictarlo; un error que desaparece mientras se busca el bolígrafo es peor que
 * ninguno.
 */
const DURACION: Record<TipoAviso, number | null> = { exito: 5000, info: 5000, error: null }

/** Como mucho tres a la vez: el cuarto empuja fuera al más viejo. */
export const MAXIMO_AVISOS = 3

type Oyente = (avisos: Aviso[]) => void

let avisos: Aviso[] = []
let siguienteId = 1
const oyentes = new Set<Oyente>()

function emitir() {
  for (const o of oyentes) o(avisos)
}

export function avisar(nuevo: NuevoAviso): number {
  const id = siguienteId++
  const aviso: Aviso = { ...nuevo, id, duracion: nuevo.duracion ?? DURACION[nuevo.tipo] }
  avisos = [...avisos, aviso].slice(-MAXIMO_AVISOS)
  emitir()
  return id
}

export function cerrarAviso(id: number) {
  if (!avisos.some((a) => a.id === id)) return
  avisos = avisos.filter((a) => a.id !== id)
  emitir()
}

export function suscribirAvisos(oyente: Oyente): () => void {
  oyentes.add(oyente)
  oyente(avisos)
  return () => {
    oyentes.delete(oyente)
  }
}

/** Sólo para las pruebas: cada una empieza sin avisos de la anterior. */
export function vaciarAvisos() {
  avisos = []
  emitir()
}
